export interface Neighborhood {
  id: string
  name: string
  region: 'Central' | 'North' | 'South' | 'East' | 'West'
  character: string
  description: string
  highlights: string[]
  bestFor: string[]
  demographics: string
  accessibility: string
}

export const neighborhoods: Neighborhood[] = [
  {
    id: 'karen',
    name: 'Karen',
    region: 'South',
    character: 'Colonial Elegance & Refined Living',
    description:
      'Named after Karen Blixen, this exclusive suburb epitomizes sophisticated living with sprawling estates, lush gardens, and a serene atmosphere. Home to diplomats, executives, and discerning residents who value privacy and prestige.',
    highlights: [
      'Exclusive residential estates with expansive properties',
      'Karen Blixen Museum and Giraffe Centre',
      'Premium shopping at The Hub Karen',
      'Fine dining establishments and boutique cafes',
      'International schools and country clubs',
    ],
    bestFor: [
      'Luxury Living',
      'Family Residences',
      'Diplomatic Community',
      'Equestrian Lifestyle',
    ],
    demographics:
      'Predominantly expatriate and affluent Kenyan families, with a strong international community presence.',
    accessibility:
      'Well-connected via Ngong Road and Karen Road. Private transport recommended. Uber and Bolt readily available.',
  },
  {
    id: 'westlands',
    name: 'Westlands',
    region: 'West',
    character: 'Cosmopolitan Energy & Modern Sophistication',
    description:
      "Nairobi's premier business and entertainment district, where contemporary luxury meets vibrant urban energy. A dynamic hub of commerce, culture, and nightlife.",
    highlights: [
      'Sarit Centre and Westgate Mall',
      'Thriving restaurant and bar scene',
      'Modern office complexes',
      'International hotels and serviced apartments',
      'Proximity to UN headquarters',
    ],
    bestFor: ['Urban Professionals', 'Entertainment', 'Business', 'Nightlife'],
    demographics:
      'Young professionals, expatriates, and business executives seeking convenience and cosmopolitan lifestyle.',
    accessibility:
      'Excellent connectivity. Major matatu routes, Uber/Bolt hub. Walking distance to many amenities.',
  },
  {
    id: 'kilimani',
    name: 'Kilimani',
    region: 'West',
    character: 'Contemporary Urban Elegance',
    description:
      'A sophisticated residential and commercial district featuring modern high-rise apartments, upscale shopping, and refined dining. The epitome of contemporary Nairobi living.',
    highlights: [
      'Yaya Centre and modern shopping complexes',
      'High-rise luxury apartments with city views',
      'Diverse international cuisine scene',
      'Proximity to Nairobi Arboretum',
      'Vibrant café culture',
    ],
    bestFor: [
      'Modern Living',
      'Young Professionals',
      'Urban Lifestyle',
      'Convenience',
    ],
    demographics:
      'Cosmopolitan mix of young professionals, expatriates, and affluent families seeking urban convenience.',
    accessibility:
      'Excellent public transport links. Matatu routes along Ngong Road. Well-served by ride-hailing services.',
  },
  {
    id: 'kileleshwa',
    name: 'Kileleshwa',
    region: 'West',
    character: 'Refined Residential Tranquility',
    description:
      'An exclusive residential enclave offering serene living with tree-lined streets, elegant homes, and a peaceful atmosphere. A haven for those seeking refined suburban comfort.',
    highlights: [
      'Quiet, tree-lined residential streets',
      'Proximity to Lavington and Kilimani amenities',
      'Exclusive apartment complexes and townhouses',
      'Easy access to international schools',
      'Secure, gated communities',
    ],
    bestFor: [
      'Family Living',
      'Peaceful Environment',
      'Professional Residents',
      'Security',
    ],
    demographics:
      'Affluent professionals and families valuing tranquility and exclusivity.',
    accessibility:
      'Well-connected via Mandera Road and Ngong Road. Private transport and ride-hailing services recommended.',
  },
  {
    id: 'lavington',
    name: 'Lavington',
    region: 'West',
    character: 'Established Prestige & Timeless Elegance',
    description:
      "One of Nairobi's most prestigious addresses, featuring grand colonial-era homes, mature gardens, and an atmosphere of refined sophistication. A neighborhood steeped in history and distinction.",
    highlights: [
      'Historic colonial architecture',
      'Expansive properties with mature gardens',
      'Valley Arcade shopping center',
      'Exclusive residential character',
      'Proximity to premium schools',
    ],
    bestFor: [
      'Luxury Estates',
      'Established Families',
      'Heritage Living',
      'Privacy',
    ],
    demographics:
      'Long-established affluent families, diplomats, and high-net-worth individuals.',
    accessibility:
      'Private transport essential. Well-served by ride-hailing services. Quiet residential streets.',
  },
  {
    id: 'muthaiga',
    name: 'Muthaiga',
    region: 'North',
    character: 'Elite Exclusivity & Distinguished Heritage',
    description:
      "Nairobi's most exclusive residential area, home to the prestigious Muthaiga Country Club. A neighborhood synonymous with wealth, power, and refined living.",
    highlights: [
      'Muthaiga Country Club - members only',
      'Sprawling estates with exceptional privacy',
      'Proximity to Karura Forest',
      'Diplomatic residences',
      'Ultra-exclusive community',
    ],
    bestFor: [
      'Ultra-Luxury Living',
      'Diplomatic Residences',
      'Elite Society',
      'Maximum Privacy',
    ],
    demographics:
      "Kenya's elite, diplomats, business magnates, and high-ranking government officials.",
    accessibility:
      'Private transport essential. Gated community with restricted access. Uber/Bolt available.',
  },
  {
    id: 'runda',
    name: 'Runda',
    region: 'North',
    character: 'Gated Luxury & Modern Prestige',
    description:
      'An ultra-exclusive gated community offering maximum security, modern luxury estates, and unparalleled privacy. The pinnacle of contemporary secure living.',
    highlights: [
      'Fully gated and secured estate',
      'Modern luxury mansions',
      'Village Market shopping complex nearby',
      'International school access',
      'Golf course proximity',
    ],
    bestFor: [
      'Maximum Security',
      'Luxury Estates',
      'Expatriate Families',
      'Privacy',
    ],
    demographics:
      'Wealthy expatriates, business executives, and affluent Kenyan families.',
    accessibility:
      'Private transport required. Controlled access. Well-served by ride-hailing within estate.',
  },
  {
    id: 'gigiri',
    name: 'Gigiri',
    region: 'North',
    character: 'Diplomatic Quarter & International Prestige',
    description:
      'Home to the United Nations complex and numerous embassies, Gigiri represents international sophistication with exceptional security and exclusive residential options.',
    highlights: [
      'UN headquarters and diplomatic missions',
      'Village Market shopping and entertainment',
      'International schools',
      'High-security residential compounds',
      'Cosmopolitan community',
    ],
    bestFor: [
      'Diplomatic Community',
      'International Professionals',
      'Security',
      'Expatriate Living',
    ],
    demographics:
      'Diplomats, UN staff, international organization employees, and expatriate families.',
    accessibility:
      'Excellent security infrastructure. Private transport recommended. Uber/Bolt available.',
  },
  {
    id: 'spring-valley',
    name: 'Spring Valley',
    region: 'West',
    character: 'Serene Sophistication & Garden Living',
    description:
      'A peaceful, upscale residential area known for its lush greenery, spacious properties, and tranquil atmosphere. Perfect for those seeking refined suburban living.',
    highlights: [
      'Quiet, leafy residential streets',
      'Spacious properties with gardens',
      'Proximity to Westlands amenities',
      'Secure neighborhood',
      'Family-friendly environment',
    ],
    bestFor: [
      'Family Living',
      'Garden Estates',
      'Peaceful Environment',
      'Suburban Comfort',
    ],
    demographics:
      'Affluent families and professionals seeking tranquil residential living.',
    accessibility:
      'Well-connected to Westlands. Private transport recommended. Ride-hailing services available.',
  },
  {
    id: 'rosslyn',
    name: 'Rosslyn',
    region: 'North',
    character: 'Modern Luxury & Valley Views',
    description:
      'An upscale residential area offering modern apartments and estates with stunning valley views. A perfect blend of contemporary living and natural beauty.',
    highlights: [
      'Modern high-rise apartments',
      'Scenic valley views',
      'Proximity to Gigiri and Village Market',
      'Secure residential complexes',
      'Growing commercial hub',
    ],
    bestFor: [
      'Modern Apartments',
      'Professionals',
      'Scenic Living',
      'Convenience',
    ],
    demographics:
      'Young professionals, expatriates, and families seeking modern amenities.',
    accessibility:
      'Good road connections. Matatu routes available. Well-served by ride-hailing services.',
  },
]

export const regions = ['All', 'Central', 'North', 'South', 'East', 'West']

export function getNeighborhoodById(id: string): Neighborhood | undefined {
  return neighborhoods.find((n) => n.id === id)
}

export function getNeighborhoodsByRegion(region: string): Neighborhood[] {
  if (region === 'All') {
    return neighborhoods
  }
  return neighborhoods.filter((n) => n.region === region)
}
